import React, { FC } from 'react';
import { MobileCategoriesWidget } from 'src/common/components/MobileCategoriesWidget';
import { useResetRedirectUrl } from 'src/common/guards/useResetRedirectUrl';
import styled, { css } from 'styled-components';
import { media } from 'src/config/breakpoints';
import { HomepageRoute } from 'src/public/publicRoutes';
import { Layout } from 'src/common/components/Layout';
import { LayoutPanelCategories } from 'src/common/components/Layout/LayoutPanelCategories';
import { MenuItem } from 'src/common/components/Layout/config';
import { LayoutBox } from 'src/common/components/LayoutBox';
import { useSpeakerQuery } from 'src/common/hooks/speaker/useSpeakerQuery';
import { SimpleContentLoader } from 'src/common/components/SimpleContentLoader';
import { SpeakerProfileCard } from 'src/speakers/components/SpeakerPage/SpeakerProfileCard';
import { useSpeakerRelatedCourses } from 'src/common/hooks/course/useSpeakerRelatedCourses';
import { SpeakerPageLatestCourses } from 'src/speakers/components/SpeakerPage/SpeakerPageLatestCoursesSection';
import { useGatsbyLikeNavigate } from 'src/common/migration/useGatsbyLikeNavigate';
import { getSpeakerById, speakersGenerator } from 'src/common/services/speakers';
import { VendorGetStaticProps } from 'vendor/utils/VendorGetStaticProps';
import all from 'it-all';
import { GetStaticPathsResult } from 'next';
import { getSpeakersItemPageRoute } from 'src/speakers/routes';
import { useRouter } from 'next/router';
import { pageParam } from 'src/lib/router.utils';
import { QueryKey } from 'src/config/queryKey';
import { isErrorResponse } from 'src/common/models/error';
import { StatusCodes } from 'http-status-codes';
import { coerceInt } from 'src/lib/coerceInt';

const Container = styled.div`
  padding: 0.75rem 0 1.25rem;
  position: relative;

  ${media.w.min.px768(css`
    padding: 0.625rem 1.5rem 7.9375rem;
  `)}

  ${media.w.min.px992(css`
    padding-top: 3.125rem;
  `)}

  ${media.w.min.px1200(css`
    align-items: flex-end;
    display: flex;
    flex-direction: column;
    padding: 4rem 0 6.875rem;
  `)}
`;

const Section = styled.div`
  width: 100%;

  ${media.w.min.px1200(css`
    margin: 0 4.31vw;
    max-width: 42.75rem;
  `)};

  ${media.w.min.px1440(css`
    margin: 0 3.875rem;
  `)}
`;

export const leftMenuItems: MenuItem[] = [
  { label: '<strong>Strona główna</strong>', url: HomepageRoute },
];

export const SpeakerPage: FC = () => {
  const navigate = useGatsbyLikeNavigate();
  const router = useRouter();

  const speakerIdParam = pageParam(router.query.speakerId);
  const speakerId = speakerIdParam ? coerceInt(speakerIdParam) : undefined;

  useResetRedirectUrl();

  const { isLoading: isSpeakerLoading, data: speaker } = useSpeakerQuery(speakerId);

  const { isLoading: isRelatedCoursesLoading, data: relatedCourses } =
    useSpeakerRelatedCourses(speakerId);

  if (!speakerId) {
    navigate(HomepageRoute);
    return <></>;
  }

  return (
    <Layout
      leftMenuItems={leftMenuItems}
      leftPanel={<LayoutPanelCategories />}
      rightMenuItems={null}
      rightPanel={null}
      title={speaker ? `${speaker.firstName} ${speaker.lastName}` : 'Wykładowca'}
      wideContent
      widgetPadding={65}
    >
      <LayoutBox>
        <Container>
          {isSpeakerLoading && <SimpleContentLoader />}
          {speaker && (
            <>
              <SpeakerProfileCard speaker={speaker} />
              <Section>
                {isRelatedCoursesLoading && <SimpleContentLoader />}
                {!isRelatedCoursesLoading && relatedCourses && (
                  <SpeakerPageLatestCourses speakerId={speakerId} courseExcerpts={relatedCourses} />
                )}
              </Section>
            </>
          )}
        </Container>
      </LayoutBox>

      <MobileCategoriesWidget />
    </Layout>
  );
};

export const getStaticProps = VendorGetStaticProps(async (ctx) => {
  try {
    const speakerIdParam = pageParam(ctx.params?.speakerId);

    const speakerId = speakerIdParam ? coerceInt(speakerIdParam) : undefined;

    if (!speakerId) {
      return {
        redirect: {
          permanent: true,
          destination: HomepageRoute,
        },
      };
    }

    await ctx.queryClient.fetchQuery([QueryKey.Speaker, speakerId], () =>
      getSpeakerById(speakerId),
    );

    return {
      props: {},
    };
  } catch (error) {
    if (isErrorResponse(error) && error.status === StatusCodes.NOT_FOUND) {
      return {
        notFound: true,
      };
    }
  }

  return {
    redirect: {
      destination: HomepageRoute,
      permanent: false,
    },
  };
});

export async function getStaticPaths(): Promise<GetStaticPathsResult<{ speakerId: string }>> {
  const speakers = await all(speakersGenerator({}));
  return {
    paths: speakers.map((speaker) => {
      return {
        params: { speakerId: String(speaker.id) },
      };
    }),
    fallback: 'blocking',
  };
}

export async function getSitemap(): Promise<SitemapEntry[]> {
  const speakers = await all(speakersGenerator({}));
  return speakers.map((speaker) => {
    const sitemapEntry: SitemapEntry = {
      loc: getSpeakersItemPageRoute(String(speaker.id)),
    };
    if (speaker.photo) {
      sitemapEntry.image = {
        image: {
          loc: speaker.photo.url,
        },
      };
    }
    return sitemapEntry;
  });
}

export default SpeakerPage;
